import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import PageHeader from '../../components/common/PageHeader';
import Badge from '../../components/common/Badge';
import { useAuth } from '../../context/AuthContext';
import useRole from '../../hooks/useRole';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { HiOutlinePencil } from 'react-icons/hi2';
import { MdDelete } from 'react-icons/md';

const DAYS = ['일', '월', '화', '수', '목', '금', '토'];
const DAY_MAP = ['일', '월', '화', '수', '목', '금', '토'];

const CalendarPage = () => {
  const { currentUser, userData } = useAuth();
  const { canEdit, isStudent, isParent } = useRole();
  const isChildView = isStudent || isParent;
  const themeColor = isParent ? '#AB47BC' : isStudent ? '#22C55E' : 'var(--color-primary)';
  const badgeVariant = isParent ? 'subjectPurple' : isStudent ? 'subjectGreen' : 'subject';
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().getDate());
  const [events, setEvents] = useState([]);
  const [classSchedules, setClassSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'week'

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({});

  // 주간 블록 클릭 팝업
  const [blockPopup, setBlockPopup] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, year, month]);

  const loadData = async () => {
    try {
      const monthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const monthEnd = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDate).padStart(2, '0')}`;
      const eventSnap = await getDocs(
        query(collection(db, 'academyEvents'), where('date', '>=', monthStart), where('date', '<=', monthEnd))
      );
      setEvents(eventSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      if (currentUser?.uid) {
        const uid = userData?.uid || currentUser.uid;
        const lookupUid = isParent ? (userData?.children?.[0] || uid) : uid;
        const classQ = isChildView
          ? query(collection(db, 'classes'), where('studentIds', 'array-contains', lookupUid))
          : query(collection(db, 'classes'), where('teacherId', '==', uid));
        const classSnap = await getDocs(classQ);
        const schedules = [];
        classSnap.docs.forEach(d => {
          const cls = d.data();
          (cls.schedule || []).forEach(sch => {
            schedules.push({
              className: cls.name,
              color: cls.color || '#4A7CFE',
              ...sch,
            });
          });
          (cls.supplements || []).forEach(sup => {
            schedules.push({
              className: cls.name,
              color: cls.color || '#4A7CFE',
              day: '__supplement__',
              date: sup.date,
              time: sup.time || '',
              endTime: sup.endTime || '',
              type: '보강',
            });
          });
        });
        setClassSchedules(schedules);
      }
    } catch (err) {
      console.error('캘린더 데이터 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= lastDate; i++) days.push(i);

  const selectedKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDate).padStart(2, '0')}`;
  const selectedDayOfWeek = new Date(year, month, selectedDate).getDay();
  const dayName = DAY_MAP[selectedDayOfWeek];

  const dateEvents = events.filter(e => e.date === selectedKey);
  const dateSchedules = classSchedules.filter(s => s.day === dayName || (s.day === '__supplement__' && s.date === selectedKey));

  const eventCountByDay = {};
  events.forEach(e => {
    const d = e.date?.split('-')[2];
    if (d) {
      const day = parseInt(d);
      eventCountByDay[day] = (eventCountByDay[day] || 0) + 1;
    }
  });

  const getContentBreakdown = (day) => {
    if (!day) return null;
    const dow = DAY_MAP[new Date(year, month, day).getDay()];
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const regular = classSchedules.filter(s => s.day === dow && s.day !== '__supplement__').length;
    const supplement = classSchedules.filter(s => s.day === '__supplement__' && s.date === dateKey).length;
    const event = eventCountByDay[day] || 0;
    if (regular + supplement + event === 0) return null;
    return { regular, supplement, event };
  };

  // === 주간 뷰 관련 ===
  const getWeekDates = () => {
    const base = new Date(year, month, selectedDate);
    const dayOfWeek = base.getDay(); // 0=일
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() - dayOfWeek + i);
      dates.push(d);
    }
    return dates;
  };
  const weekDates = useMemo(getWeekDates, [year, month, selectedDate]);

  const prevWeek = () => {
    const d = new Date(year, month, selectedDate - 7);
    setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1));
    setSelectedDate(d.getDate());
  };
  const nextWeek = () => {
    const d = new Date(year, month, selectedDate + 7);
    setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1));
    setSelectedDate(d.getDate());
  };

  const getSchedulesForDate = (date) => {
    const dow = DAY_MAP[date.getDay()];
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return classSchedules.filter(s => s.day === dow || (s.day === '__supplement__' && s.date === dateKey));
  };

  const getEventsForDate = (date) => {
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return events.filter(e => e.date === key);
  };

  // 주간 뷰에서 사용할 시간 범위 계산
  const weekTimeSlots = useMemo(() => {
    let minHour = 24, maxHour = 0;
    weekDates.forEach(date => {
      const scheds = getSchedulesForDate(date);
      scheds.forEach(s => {
        const st = s.startTime || s.time || '';
        if (st) {
          const h = parseInt(st.split(':')[0]);
          if (h < minHour) minHour = h;
          if (h + 1 > maxHour) maxHour = h + 2;
        }
      });
      const evts = getEventsForDate(date);
      evts.forEach(e => {
        if (e.time) {
          const h = parseInt(e.time.split(':')[0]);
          if (h < minHour) minHour = h;
          if (h + 1 > maxHour) maxHour = h + 2;
        }
      });
    });
    if (minHour >= maxHour) { minHour = 8; maxHour = 22; }
    minHour = Math.max(0, minHour - 1);
    maxHour = Math.min(24, maxHour + 1);
    const slots = [];
    for (let h = minHour; h < maxHour; h++) slots.push(h);
    return slots;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDates, classSchedules, events]);

  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const parts = timeStr.split(':');
    return parseInt(parts[0]) * 60 + parseInt(parts[1] || 0);
  };

  const today = new Date();
  const isToday = (date) =>
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  const formatWeekRange = () => {
    const s = weekDates[0];
    const e = weekDates[6];
    return `${s.getMonth() + 1}월 ${s.getDate()}일 ~ ${e.getMonth() + 1}월 ${e.getDate()}일`;
  };

  const handleAddEvent = async () => {
    if (!formData.title || !formData.date) return;
    const data = {
      title: formData.title,
      date: formData.date,
      time: formData.time || '',
      type: formData.type || '기타',
      detail: formData.detail || '',
      color: formData.type === '수업' ? 'var(--color-primary)' : formData.type === '시험' ? '#EF5350' : '#42A5F5',
      createdBy: currentUser?.uid || '',
    };
    if (editingEvent) {
      await updateDoc(doc(db, 'academyEvents', editingEvent.id), data);
    } else {
      await addDoc(collection(db, 'academyEvents'), data);
    }
    setShowModal(false);
    setEditingEvent(null);
    setFormData({});
    loadData();
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await deleteDoc(doc(db, 'academyEvents', eventId));
    loadData();
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      date: event.date,
      time: event.time,
      type: event.type,
      detail: event.detail,
    });
    setShowModal(true);
  };

  return (
    <Container>
      <PageHeader
        title="캘린더"
        showBack={false}
        rightAction={canEdit ? <AddButton onClick={() => { setEditingEvent(null); setFormData({ date: selectedKey }); setShowModal(true); }}>+ 일정 추가</AddButton> : null}
      />

      <ViewToggle>
        <ToggleBtn $active={viewMode === 'month'} onClick={() => setViewMode('month')}>월간</ToggleBtn>
        <ToggleBtn $active={viewMode === 'week'} onClick={() => setViewMode('week')}>주간</ToggleBtn>
      </ViewToggle>

      {viewMode === 'month' ? (
        <>
          <CalendarBox>
            <MonthNav>
              <NavBtn onClick={prevMonth}><IoChevronBack size={20} /></NavBtn>
              <MonthTitle>{year}년 {month + 1}월</MonthTitle>
              <NavBtn onClick={nextMonth}><IoChevronForward size={20} /></NavBtn>
            </MonthNav>

            <DayHeader>
              {DAYS.map((d, i) => (
                <DayLabel key={d} $sunday={i === 0} $saturday={i === 6}>{d}</DayLabel>
              ))}
            </DayHeader>

            <DayGrid>
              {days.map((day, i) => (
                <DayCell
                  key={i}
                  $active={day === selectedDate}
                  $empty={!day}
                  $color={themeColor}
                  onClick={() => day && setSelectedDate(day)}
                >
                  {day && (() => {
                    const bd = getContentBreakdown(day);
                    return (
                      <>
                        <DayNum $sunday={i % 7 === 0} $saturday={i % 7 === 6}>{day}</DayNum>
                        {bd && (
                          <DayTags>
                            {bd.regular > 0 && <DayTag $color={themeColor}>정규{bd.regular}</DayTag>}
                            {bd.supplement > 0 && <DayTag $color="#F59E0B">보강{bd.supplement}</DayTag>}
                            {bd.event > 0 && <DayTag $color="#EF5350">일정{bd.event}</DayTag>}
                          </DayTags>
                        )}
                      </>
                    );
                  })()}
                </DayCell>
              ))}
            </DayGrid>
            <DayLegend>
              <LegendItem><LegendDot $color={themeColor} />정규수업</LegendItem>
              <LegendItem><LegendDot $color="#F59E0B" />보강수업</LegendItem>
              <LegendItem><LegendDot $color="#EF5350" />학원일정</LegendItem>
            </DayLegend>
          </CalendarBox>

          <EventSection>
            <EventTitle>{month + 1}월 {selectedDate}일 ({dayName}) 일정</EventTitle>

            {dateSchedules.map((sch, i) => (
              <EventCard key={`sch-${i}`}>
                <EventLeft>
                  <Badge $variant={badgeVariant}>{sch.type === '보강' ? '보강수업' : '정규수업'}</Badge>
                  <EventTime $color={themeColor}>{sch.time || ''}{sch.endTime ? ` ~ ${sch.endTime}` : ''}</EventTime>
                  <EventName>{sch.className}</EventName>
                  {sch.room && <EventDetail>{sch.room}</EventDetail>}
                </EventLeft>
              </EventCard>
            ))}

            {dateEvents.map(evt => (
              <EventCard key={evt.id} $event>
                <EventLeft>
                  <Badge $variant={evt.type === '시험' ? 'urgent' : 'subject'}>{evt.type || '기타'}</Badge>
                  {evt.time && <EventTime>{evt.time}</EventTime>}
                  <EventName>{evt.title}</EventName>
                  {evt.detail && <EventDetail>{evt.detail}</EventDetail>}
                </EventLeft>
                {canEdit && (
                  <EventActions>
                    <EditBtn onClick={() => openEditModal(evt)}><HiOutlinePencil size={18} /></EditBtn>
                    <DelBtn onClick={() => handleDeleteEvent(evt.id)}><MdDelete size={18} /></DelBtn>
                  </EventActions>
                )}
              </EventCard>
            ))}

            {dateSchedules.length === 0 && dateEvents.length === 0 && (
              <EmptyText>일정이 없습니다.</EmptyText>
            )}
          </EventSection>
        </>
      ) : (
        /* ====== 주간 뷰 ====== */
        <>
          <WeekNavBox>
            <MonthNav>
              <NavBtn onClick={prevWeek}><IoChevronBack size={20} /></NavBtn>
              <MonthTitle>{formatWeekRange()}</MonthTitle>
              <NavBtn onClick={nextWeek}><IoChevronForward size={20} /></NavBtn>
            </MonthNav>
          </WeekNavBox>

          <WeekBody>
            <WeekHeader>
              <WeekTimeCol />
              {weekDates.map((date, i) => (
                <WeekDayCol key={i} $today={isToday(date)}>
                  <WeekDayLabel $sunday={date.getDay() === 0} $saturday={date.getDay() === 6}>
                    {DAYS[date.getDay()]}
                  </WeekDayLabel>
                  <WeekDateNum $today={isToday(date)} $sunday={date.getDay() === 0} $saturday={date.getDay() === 6}>{date.getDate()}</WeekDateNum>
                </WeekDayCol>
              ))}
            </WeekHeader>

            <WeekGrid>
              <WeekTimeLabelCol>
                {weekTimeSlots.map(h => (
                  <WeekTimeLabel key={h}>{String(h).padStart(2, '0')}:00</WeekTimeLabel>
                ))}
              </WeekTimeLabelCol>

              {weekDates.map((date, colIdx) => {
                const scheds = getSchedulesForDate(date);
                const evts = getEventsForDate(date);
                const items = [];

                scheds.forEach((s) => {
                  const startMin = parseTime(s.startTime || s.time);
                  const endMin = s.endTime ? parseTime(s.endTime) : (startMin != null ? startMin + 60 : null);
                  if (startMin != null) {
                    const startT = s.startTime || s.time || '';
                    const endT = s.endTime || '';
                    items.push({ type: 'class', label: s.className, color: s.color, startMin, endMin, room: s.room, timeStr: `${startT}${endT ? ' - ' + endT : ''}` });
                  }
                });

                evts.forEach((e) => {
                  const startMin = parseTime(e.time);
                  if (startMin != null) {
                    items.push({ type: 'event', label: e.title, color: e.color || '#42A5F5', startMin, endMin: startMin + 60, evtType: e.type, detail: e.detail, timeStr: e.time });
                  }
                });

                return (
                  <WeekDayColumn key={colIdx} $today={isToday(date)}>
                    {weekTimeSlots.map(h => (
                      <WeekSlot key={h} />
                    ))}
                    {items.map((item, idx) => {
                      const slotStart = weekTimeSlots[0] * 60;
                      const top = ((item.startMin - slotStart) / 60) * 52;
                      const height = Math.max(((item.endMin - item.startMin) / 60) * 52, 26);
                      return (
                        <WeekBlock
                          key={idx}
                          $color={item.color}
                          style={{ top: `${top}px`, height: `${height}px` }}
                          onClick={() => setBlockPopup(item)}
                        >
                          <WeekBlockText>{item.label}</WeekBlockText>
                          {item.room && <WeekBlockSub>{item.room}</WeekBlockSub>}
                          {item.timeStr && <WeekBlockSub>{item.timeStr}</WeekBlockSub>}
                        </WeekBlock>
                      );
                    })}
                  </WeekDayColumn>
                );
              })}
            </WeekGrid>
          </WeekBody>
        </>
      )}

      {blockPopup && (
        <ModalOverlay onClick={() => setBlockPopup(null)}>
          <BlockPopupModal onClick={e => e.stopPropagation()}>
            <BlockPopupBar $color={blockPopup.color} />
            <BlockPopupBody>
              <BlockPopupTitle>{blockPopup.label}</BlockPopupTitle>
              {blockPopup.timeStr && <BlockPopupInfo>{blockPopup.timeStr}</BlockPopupInfo>}
              {blockPopup.room && <BlockPopupInfo>{blockPopup.room}</BlockPopupInfo>}
              {blockPopup.type === 'class' && <BlockPopupInfo>정규수업</BlockPopupInfo>}
              {blockPopup.evtType && <BlockPopupInfo>{blockPopup.evtType}</BlockPopupInfo>}
              {blockPopup.detail && <BlockPopupDetail>{blockPopup.detail}</BlockPopupDetail>}
              <BlockPopupClose onClick={() => setBlockPopup(null)}>닫기</BlockPopupClose>
            </BlockPopupBody>
          </BlockPopupModal>
        </ModalOverlay>
      )}

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingEvent ? '일정 수정' : '일정 추가'}</ModalTitle>
              <CloseBtn onClick={() => setShowModal(false)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <FieldLabel>일정 제목 *</FieldLabel>
              <FieldInput
                placeholder=""
                value={formData.title || ''}
                onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
              />
              <FieldLabel>일정 유형 *</FieldLabel>
              <FieldSelect
                value={formData.type || '기타'}
                onChange={e => setFormData(p => ({ ...p, type: e.target.value }))}
              >
                <option value="수업">수업</option>
                <option value="시험">시험</option>
                <option value="상담">상담</option>
                <option value="기타">기타</option>
              </FieldSelect>
              <FieldLabel>날짜 *</FieldLabel>
              <FieldInput
                type="date"
                value={formData.date || ''}
                onChange={e => setFormData(p => ({ ...p, date: e.target.value }))}
              />
              <FieldLabel>시간</FieldLabel>
              <FieldInput
                type="time"
                value={formData.time || ''}
                onChange={e => setFormData(p => ({ ...p, time: e.target.value }))}
              />
              <FieldLabel>상세 내용</FieldLabel>
              <FieldTextarea
                placeholder=""
                value={formData.detail || ''}
                onChange={e => setFormData(p => ({ ...p, detail: e.target.value }))}
              />
              <ModalButtons>
                <CancelBtn onClick={() => setShowModal(false)}>취소</CancelBtn>
                <SubmitBtn onClick={handleAddEvent}>{editingEvent ? '수정' : '추가'}</SubmitBtn>
              </ModalButtons>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default CalendarPage;

const Container = styled.div`
  background: ${({ theme }) => theme.colors.background.default};
  min-height: 100vh;
  padding-bottom: 80px;
`;

const AddButton = styled.button`
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid rgba(255,255,255,0.5);
  color: white;
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const ViewToggle = styled.div`
  display: flex;
  margin: 12px 16px 0;
  background: #F3F4F6;
  border-radius: 10px;
  padding: 3px;
`;

const ToggleBtn = styled.button`
  flex: 1;
  padding: 8px 0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: ${({ $active }) => $active ? 700 : 400};
  color: ${({ $active }) => $active ? '#111827' : '#9CA3AF'};
  background: ${({ $active }) => $active ? 'white' : 'transparent'};
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  transition: all 0.2s;
`;

const CalendarBox = styled.div`
  padding: 16px;
  background: white;
`;

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const NavBtn = styled.button`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const MonthTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
`;

const DayHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  margin-bottom: 8px;
`;

const DayLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ $sunday, $saturday, theme }) =>
    $sunday ? '#EF5350' : $saturday ? '#42A5F5' : theme.colors.text.secondary};
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const DayCell = styled.button`
  min-height: 52px;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ $active, $color }) => $active ? ($color ? `${$color}15` : 'var(--color-primary-light)') : 'transparent'};
  border: ${({ $active, $color }) => $active ? `2px solid ${$color || 'var(--color-primary)'}` : '2px solid transparent'};
  pointer-events: ${({ $empty }) => $empty ? 'none' : 'auto'};
`;

const DayNum = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ $sunday, $saturday }) => $sunday ? '#EF5350' : $saturday ? '#42A5F5' : 'inherit'};
`;

const DayTags = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 1px;
`;

const DayTag = styled.span`
  font-size: 8px;
  font-weight: 900;
  color: ${({ $color }) => $color};
  line-height: 1.1;
`;

const DayLegend = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 8px 0 4px;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #6B7280;
`;

const LegendDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const EventSection = styled.div`
  padding: 16px;
`;

const EventTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  margin-bottom: 12px;
`;

const EventCard = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadow.small};
  margin-bottom: 10px;
  border: none;
`;

const EventLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const EventTime = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ $color }) => $color || '#6B7280'};
  font-weight: 600;
`;

const EventName = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const EventDetail = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EventActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EditBtn = styled.button`
  color: ${({ theme }) => theme.colors.text.hint};
`;

const DelBtn = styled.button`
  color: ${({ theme }) => theme.colors.text.hint};
`;

const EmptyText = styled.p`
  text-align: center;
  padding: 24px;
  color: ${({ theme }) => theme.colors.text.hint};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

/* ====== 주간 뷰 ====== */
const WeekNavBox = styled.div`
  padding: 16px 16px 8px;
  background: white;
`;

const WeekHeader = styled.div`
  display: flex;
  border-bottom: 1px solid #F0F0F0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 2;
`;

const WeekTimeCol = styled.div`
  width: 44px;
  flex-shrink: 0;
`;

const WeekDayCol = styled.div`
  flex: 1;
  text-align: center;
  padding: 6px 0;
`;

const WeekDayLabel = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $sunday, $saturday }) => $sunday ? '#EF5350' : $saturday ? '#42A5F5' : '#9CA3AF'};
`;

const WeekDateNum = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 2px auto 0;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $today, $sunday, $saturday }) => $today ? 'white' : $sunday ? '#EF5350' : $saturday ? '#42A5F5' : '#111827'};
  background: ${({ $today, $sunday, $saturday }) => $today ? ($sunday ? '#EF5350' : $saturday ? '#42A5F5' : 'var(--color-primary)') : 'transparent'};
`;

const WeekBody = styled.div`
  background: white;
`;

const WeekGrid = styled.div`
  display: flex;
  min-height: 300px;
`;

const WeekTimeLabelCol = styled.div`
  width: 44px;
  flex-shrink: 0;
  border-right: 1px solid #F0F0F0;
`;

const WeekTimeLabel = styled.div`
  height: 52px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  color: #9CA3AF;
  padding-top: 2px;
`;

const WeekDayColumn = styled.div`
  flex: 1;
  position: relative;
  border-right: 1px solid #F5F5F5;
  background: white;
  &:last-child { border-right: none; }
`;

const WeekSlot = styled.div`
  height: 52px;
  border-bottom: 1px solid #F5F5F5;
`;

const WeekBlock = styled.div`
  position: absolute;
  left: 1px;
  right: 1px;
  background: ${({ $color }) => $color || 'var(--color-primary)'};
  border-radius: 4px;
  padding: 2px 3px;
  overflow: hidden;
  z-index: 1;
  opacity: 0.9;
  cursor: pointer;
  &:hover { opacity: 1; }
`;

const WeekBlockText = styled.span`
  display: block;
  font-size: 10px;
  font-weight: 700;
  color: white;
  word-break: break-all;
  line-height: 1.2;
`;

const WeekBlockSub = styled.span`
  display: block;
  font-size: 9px;
  color: rgba(255,255,255,0.8);
  word-break: break-all;
  line-height: 1.2;
`;

const BlockPopupModal = styled.div`
  width: 80%;
  max-width: 320px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  margin: auto;
`;

const BlockPopupBar = styled.div`
  height: 6px;
  background: ${({ $color }) => $color || 'var(--color-primary)'};
`;

const BlockPopupBody = styled.div`
  padding: 20px;
`;

const BlockPopupTitle = styled.p`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 12px;
`;

const BlockPopupInfo = styled.p`
  font-size: 14px;
  color: #6B7280;
  margin-bottom: 4px;
`;

const BlockPopupDetail = styled.p`
  font-size: 14px;
  color: #374151;
  margin-top: 10px;
  line-height: 1.5;
`;

const BlockPopupClose = styled.button`
  width: 100%;
  padding: 12px;
  margin-top: 16px;
  border-radius: 10px;
  background: #F3F4F6;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 480px;
  background: white;
  border-radius: 20px 20px 0 0;
  max-height: 85vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--color-primary-light);
  border-bottom: 1px solid #D9E2FF;
`;

const ModalTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: #1E3A5F;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
  background: white;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: 6px;
  margin-top: 14px;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const FieldSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const FieldTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
  min-height: 80px;
  resize: vertical;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const SubmitBtn = styled.button`
  flex: 2;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.md};
`;
